import {
  DATA_SOURCE_IDS,
  FUSION_LABELS,
  FUSION_TYPES,
  isTrialDisabledTaxonomyNode,
} from '@discover-frontend/constants';
import { TaxonomyExplorer } from '@discover-frontend/components';
import { getParentIdFromChild } from '@discover-frontend/utils';
import {
  Loading,
  SearchSelection,
  Select,
  Tab,
  Tabs,
} from '@cubeyou-team/hx-react-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { alphabeticallySortByKey, getItemPic, isEmpty } from 'src/utils/misc';
import { CATEGORY_IDS } from 'src/modules/overview/sources/instagram/constants';
import { QuerySourceContainer } from 'src/modules/query-builder/components';
import {
  getTrialFeatureForFusionSource,
  isTrialRestrictedFusionSource,
  TRIAL_FEATURES,
  TRIAL_RESTRICTED_FUSION_SOURCES,
  useTrialPaywall,
} from 'src/modules/trial-paywall';
import {
  addBlockInSelectedGroup,
  addItemWithInfosInSelectedGroup,
  changeAudienceTimeframeRequest,
  changeBaseQueryRequest,
  changeQueryTypeRequest,
  changeResultTimeframeRequest,
  getAllFolders,
  getDemographicsSearchResults,
  getExploreItems,
  getSavedQueries,
  itemDragToggle,
  loadMoreResults,
  loadMoreSavedQueries,
  showModal,
} from '../../actions';

import { getExplorerTaxonomy } from '../../selectors/taxonomy';
import { supportsSocial } from '../../utils/features';

import UnitedStatesMapChart from '../../components/Charts/UnitedStatesMapChart';
import translations from '../../lang';
import { mapDemographicItem } from '../../mappers';

import { QueryAdvancedOptions } from '../../components/QueryBuilder/components';
import { RepositoryConstants } from '../../constants/repository';
import { mapQueryBlocksWithUniqueIdAndFormat } from '../../operations/query';
import {
  getSources,
  hasGwiFusion,
  hasMriSimmonsFusion,
  hasWWGwiMediaFusion,
  hasTgiFusion,
  hasSpecificFusion,
  isAvailableQueryBuilderType,
} from '../../utils/AuthHelperMethods';

const StyledTaxonomyExplorerContainer = styled.div`
  height: 100%;

  & > div {
    height: 75px;
    align-items: end;
  }
  & [data-test='children-container'] {
    height: calc(100% - 140px);
    padding-top: 0px;
  }

  & .tab-item {
    height: 100%;
  }
`;

const StyledSearchSelection = styled.div`
  width: 92%;
  & [data-test='search-selection-div'] {
    height: 200px;
  }
`;

const StyledSelect = styled.div`
  width: 92%;
  & [data-test-selector='select'] {
    height: 200px;
  }
`;

const ADVANCED_OPTIONS_TAB_ID = 'advanced-options';

const TaxonomyExplorerContainer = () => {
  const [selectedTaxonomy, setSelectedTaxonomy] = useState('demographic');
  const [currentQ, setCurrentQ] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const dispatch = useDispatch();
  const { guardFeature, isTrial } = useTrialPaywall();
  const taxonomyList = useSelector((state) => getExplorerTaxonomy(state));
  const queryBuilder = useSelector((state) => state.queryBuilder);
  const timeframes = useSelector((state) => state.timeframes);
  const demographicsItems = useSelector((state) => state.demographicsItems);
  const exploreItems = useSelector((state) => state.exploreItems);
  const folders = useSelector((state) => state.repository.folders);
  const selectedTarget = useSelector(
    (state) => state.queryBuilder.target_selected
  );
  const dataSource = useSelector((state) => state.queryBuilder.dataSource);

  const demographicsItemsData =
    (demographicsItems.data && demographicsItems.data[dataSource]) || [];

  const isSelectedFusionSource = (source) =>
    Object.values(FUSION_TYPES).includes(source);

  const handleRefreshTaxonomy = useCallback((event) => {
    setSelectedTaxonomy('demographic');
    setCurrentQ('');
  }, []);

  useEffect(() => {
    dispatch(getAllFolders());

    window.addEventListener('REFRESH_TAXONOMY', handleRefreshTaxonomy);
    return () => {
      window.removeEventListener('REFRESH_TAXONOMY', handleRefreshTaxonomy);
    };
  }, [dispatch, handleRefreshTaxonomy]);

  const handleAddToQuery = useCallback(
    (item) => {
      if (isTrial && isTrialRestrictedFusionSource(item.source)) return;
      dispatch(addItemWithInfosInSelectedGroup(selectedTarget, item));
    },
    [dispatch, selectedTarget, isTrial]
  );

  const handleOnTaxonomySelected = useCallback(
    (categoryId) => {
      const taxonomyItem = taxonomyList.data
        .flat()
        .find((item) => item && item.id === categoryId);

      if (
        isTrialDisabledTaxonomyNode(taxonomyItem || { id: categoryId }, isTrial)
      ) {
        return;
      }

      setSelectedSource('all');
      setSelectedType('all');
      setSelectedTaxonomy(categoryId);

      if (categoryId === 'demographic') {
        setCurrentQ('');
      }

      if (categoryId === 'saved-queries' || categoryId.includes('folder_')) {
        return dispatch(
          getSavedQueries(categoryId, {
            queryType: 'all',
          })
        );
      }

      if (categoryId !== 'demographic') {
        dispatch(
          getExploreItems({
            categoryId,
            q: currentQ,
            sources: getSources(dataSource),
            variableTypes:
              dataSource !== DATA_SOURCE_IDS.TWITTER
                ? 'item,category,demographic'
                : 'item,category,psychographic,demographic',
          })
        );
      }
    },
    [dispatch, currentQ, dataSource, isTrial, taxonomyList.data]
  );

  const applySourceFilter = useCallback(
    (source) => {
      setSelectedSource(source);

      if (isSelectedFusionSource(source)) setSelectedType('all');

      dispatch(
        getExploreItems({
          sources: source,
          variableTypes: isSelectedFusionSource(source)
            ? 'item,category,psychographic,demographic'
            : selectedType,
        })
      );
    },
    [dispatch, selectedType]
  );

  const handleOnSourceFilterSelected = useCallback(
    (source) => {
      const feature = getTrialFeatureForFusionSource(source);

      if (feature) {
        guardFeature(feature, () => applySourceFilter(source));
        return;
      }

      applySourceFilter(source);
    },
    [applySourceFilter, guardFeature]
  );

  const handleOnTypeFilterSelected = useCallback(
    (categoryId, type) => {
      setSelectedType(type);
      if (categoryId === 'saved-queries' || categoryId.includes('folder_')) {
        return dispatch(
          getSavedQueries(categoryId, {
            queryType: type,
          })
        );
      }
      dispatch(
        getExploreItems({
          variableTypes: type,
        })
      );
    },
    [dispatch]
  );

  const handleOnDemographicSelected = useCallback(
    (demographic, demographicClass) => {
      const demoClass =
        demographicClass === 'uk_country'
          ? 'country'
          : demographicClass === 'uk_ethnicity'
          ? 'ethnicity'
          : demographicClass;
      const demographicItem = mapDemographicItem(demographic, demoClass);
      dispatch(
        addItemWithInfosInSelectedGroup(selectedTarget, demographicItem)
      );
    },
    [dispatch, selectedTarget]
  );

  const getFilteredDemographics = useCallback(
    (demographics, q) => {
      const mergedDemos = demographicsItemsData.reduce((arr, demo) => {
        return [...arr, ...demo.values];
      }, []);

      const filterDemos = mergedDemos.filter((demo) =>
        demo.label.toLowerCase().includes(q.toLowerCase())
      );

      return {
        ...demographics,
        data: demographicsItemsData,
        demographicsSearchResults: filterDemos.sort(
          alphabeticallySortByKey('label')
        ),
      };
    },
    [demographicsItemsData]
  );

  const handleOnItemsSearch = useCallback(
    (q) => {
      setCurrentQ(q);

      if (selectedTaxonomy === 'demographic') {
        return false;
      }

      if (
        selectedTaxonomy === 'saved-queries' ||
        selectedTaxonomy.includes('folder_')
      ) {
        return dispatch(
          getSavedQueries(selectedTaxonomy, {
            q,
          })
        );
      }

      if (selectedTaxonomy !== 'all') {
        return dispatch(
          getExploreItems({
            q,
            categoryId: selectedTaxonomy,
            sources: getSources(dataSource),
          })
        );
      }

      dispatch(
        getExploreItems({
          q,
          sources: selectedSource,
          variableTypes: selectedType,
        })
      );
    },
    [dispatch, selectedTaxonomy, selectedSource, selectedType, dataSource]
  );

  const shouldCallLoadMore = useCallback(() => {
    const { totalPageCount, params } = exploreItems;
    const { pagination } = params;

    return pagination.page < totalPageCount;
  }, [exploreItems]);

  const handleOnItemDragToggle = useCallback(
    (mode) => {
      dispatch(itemDragToggle(mode));
    },
    [dispatch]
  );

  const handleOnLoadMoreItems = useCallback(
    (params) => {
      if (
        selectedTaxonomy === 'saved-queries' ||
        selectedTaxonomy.includes('folder_')
      ) {
        return dispatch(loadMoreSavedQueries(selectedTaxonomy, params));
      }
      dispatch(loadMoreResults(params));
    },
    [dispatch, selectedTaxonomy]
  );

  const getDemographicValuesByType = useCallback((demographics, type) => {
    if (!demographics || demographics.length === 0) {
      return [];
    }

    const filtered = demographics.find((item) => item.type === type);
    const values = (filtered && filtered.values) || [];
    return values.map((item) => ({
      ...item,
      value: item.id,
    }));
  }, []);

  const getDemographicsSearchResultsByType = useCallback(
    (demographics, type) => {
      return (
        (demographics.demographicsSearchKeyResults &&
          demographics.demographicsSearchKeyResults[type] &&
          demographics.demographicsSearchKeyResults[type][dataSource]) ||
        []
      );
    },
    [dataSource]
  );

  const getSearchSelection = useCallback(
    (demographics, type) => {
      return (
        <StyledSearchSelection>
          <SearchSelection
            options={getDemographicsSearchResultsByType(demographics, type)}
            onItemSelected={(item) => handleOnDemographicSelected(item, type)}
            onItemsSearch={(value) => {
              dispatch(getDemographicsSearchResults(value, type));
            }}
            isLoading={demographics.demographicsSearchKeyLoading[type]}
          />
        </StyledSearchSelection>
      );
    },
    [dispatch, getDemographicsSearchResultsByType, handleOnDemographicSelected]
  );

  const getSelect = useCallback(
    (demographics, type) => {
      const options = getDemographicValuesByType(demographics, type);
      return (
        <StyledSelect>
          <Select
            options={options}
            onItemChange={(value) => {
              const item = options.find((item) => item.id === value);
              handleOnDemographicSelected(item, type);
            }}
            placeholder="Click and select"
          />
        </StyledSelect>
      );
    },
    [getDemographicValuesByType, handleOnDemographicSelected]
  );

  const queryPreview = useMemo(() => {
    const preview = {
      ...queryBuilder.targets[selectedTarget],
    };
    return preview;
  }, [queryBuilder, selectedTarget]);

  const handleQueryTypeChangeRequest = useCallback(
    (queryType) => {
      dispatch(changeQueryTypeRequest(queryType, selectedTarget));
    },
    [dispatch, selectedTarget]
  );

  const handleAudienceTimeframeChangeRequest = useCallback(
    (audience_timeframe) => {
      dispatch(
        changeAudienceTimeframeRequest(audience_timeframe, selectedTarget)
      );
    },
    [dispatch, selectedTarget]
  );

  const handleResultTimeframeChangeRequest = useCallback(
    (results_timeframe) => {
      dispatch(changeResultTimeframeRequest(results_timeframe, selectedTarget));
    },
    [dispatch, selectedTarget]
  );

  const handleBaseQueryChangeRequest = useCallback(
    (category) => {
      dispatch(changeBaseQueryRequest(category, selectedTarget));
    },
    [dispatch, selectedTarget]
  );

  const filteredDemographics = getFilteredDemographics(
    demographicsItems,
    currentQ
  );

  const specifiedComponents = {
    state: (
      <UnitedStatesMapChart
        data={getDemographicValuesByType(demographicsItemsData, 'state')}
        onItemClick={(item) => handleOnDemographicSelected(item, 'state')}
      />
    ),
    metro_area: getSearchSelection(demographicsItems, 'metro_area'),
    job_function: getSearchSelection(demographicsItems, 'job_function'),
    language: getSearchSelection(demographicsItems, 'language'),
    area_of_employment: getSelect(demographicsItemsData, 'area_of_employment'),
    BARB_regions: getSelect(demographicsItemsData, 'BARB_regions'),
  };

  const areAvailableTypeFilters = useMemo(() => {
    if (
      selectedTaxonomy === 'saved-queries' ||
      selectedTaxonomy.includes('folder_')
    ) {
      return isAvailableQueryBuilderType(RepositoryConstants.CONVERSATION);
    }

    if (selectedTaxonomy === 'all') return true;

    const category = getParentIdFromChild(
      taxonomyList.data.flat(),
      selectedTaxonomy
    );
    return ['-1', '-2', '-3'].includes(category);
  }, [taxonomyList, selectedTaxonomy]);

  const sourceFilters = useMemo(() => {
    if (
      !hasMriSimmonsFusion() &&
      !hasGwiFusion() &&
      !hasWWGwiMediaFusion() &&
      !hasTgiFusion()
    ) {
      return null;
    }
    const filters = [
      {
        id: 'all',
        label: 'All',
      },
      {
        id: 'twitter',
        label: 'Discover',
      },
    ];

    if (dataSource === DATA_SOURCE_IDS.TWITTER) {
      Object.keys(FUSION_TYPES).forEach((key) => {
        if (hasSpecificFusion(FUSION_TYPES[key])) {
          filters.push({
            id: FUSION_TYPES[key],
            label: FUSION_LABELS[key],
          });
        }
      });
    } else if (dataSource === DATA_SOURCE_IDS.TWITTER) {
      filters.push({
        id: 'all',
        label: 'All',
      });
    }

    return filters;
  }, [dataSource]);
  const getTypeFilters = useCallback(
    (categoryId) => {
      if (categoryId === 'saved-queries' || categoryId.includes('folder_')) {
        return [
          {
            id: 'all',
            label: 'All',
          },
          {
            id: 'interest',
            label: 'Interests',
          },
          {
            id: 'conversation',
            label: 'Conversation',
          },
        ];
      }
      return [
        {
          id: 'all',
          label: 'All',
          disabled: isSelectedFusionSource(selectedSource),
        },
        {
          id: 'item',
          label: 'Items',
          disabled: isSelectedFusionSource(selectedSource),
        },
        {
          id: 'category',
          label: 'Categories',
          disabled: isSelectedFusionSource(selectedSource),
        },
      ];
    },
    [selectedSource]
  );

  useEffect(() => setSelectedTab(0), [dataSource]);

  const filteredTaxonomyData = useMemo(() => {
    return taxonomyList.data.map((arr) =>
      arr
        .filter((item) => item.id !== CATEGORY_IDS.CONTENT_CATEGORIES)
        .map((item) => {
          const taxonomyItem = isTrialDisabledTaxonomyNode(item, isTrial)
            ? { ...item, isDisabled: true }
            : item;

          // If item has children, filter them as well
          if (Array.isArray(taxonomyItem.children)) {
            return {
              ...taxonomyItem,
              children: taxonomyItem.children
                .filter((child) => child.id !== CATEGORY_IDS.CONTENT_CATEGORIES)
                .map((child) =>
                  isTrialDisabledTaxonomyNode(child, isTrial)
                    ? { ...child, isDisabled: true }
                    : child
                ),
            };
          }
          return taxonomyItem;
        })
    );
  }, [taxonomyList.data, isTrial]);

  const tabs = useMemo(() => {
    const listOfTabs = [
      <Tab title="Audience attributes" key="audience-attributes">
        <QuerySourceContainer />
        <Loading isLoading={taxonomyList.isLoading}>
          <TaxonomyExplorer
            className="-mt-4"
            selectedDataSource={dataSource}
            disabledItemSources={
              isTrial ? [...TRIAL_RESTRICTED_FUSION_SOURCES] : undefined
            }
            exploreItems={{
              ...exploreItems,
              data: exploreItems.data.map((item) => {
                return {
                  ...item,
                  feeds:
                    !isEmpty(item.feeds) &&
                    dataSource === DATA_SOURCE_IDS.APPS &&
                    item.feeds,
                  taxonomy: item.taxonomy || [],
                  pic: getItemPic(item.pic, item.feeds),
                };
              }),
            }}
            taxonomy={[
              ...filteredTaxonomyData,
              folders.length > 0 ? [folders] : [],
            ]}
            demographics={filteredDemographics}
            currentQ={currentQ}
            shouldCallLoadMore={shouldCallLoadMore}
            onItemSelected={handleAddToQuery}
            onTaxonomySelected={handleOnTaxonomySelected}
            onDemographicSelected={handleOnDemographicSelected}
            selectedOptionId={selectedTaxonomy}
            onItemsSearch={handleOnItemsSearch}
            showModal={(props, type) => dispatch(showModal(props, type))}
            taxonomySelectorId="gtTaxonomySelector"
            onItemDragToggle={handleOnItemDragToggle}
            onLoadMoreRequest={handleOnLoadMoreItems}
            noConversationsFoundMessage={
              translations['en-US'].QUERY_BUILDER.noConversationsFound
            }
            emptyConversationResultsListTitle={
              translations['en-US'].QUERY_BUILDER.emptyConversationResultsList
                .title
            }
            emptyConversationResultsListSubtitle={
              translations['en-US'].QUERY_BUILDER.emptyConversationResultsList
                .subtitle
            }
            emptyItemResultMessage={
              translations['en-US'].QUERY_BUILDER.emptyItemResult
            }
            emptyDemographicDataMessage={
              translations['en-US'].QUERY_BUILDER.emptySearchDemosResult
            }
            specifiedComponents={specifiedComponents}
            initialSource={selectedSource}
            initialType={selectedType}
            sourceFilters={
              selectedTaxonomy === 'all' &&
              dataSource === DATA_SOURCE_IDS.TWITTER &&
              sourceFilters
            }
            typeFilters={
              areAvailableTypeFilters && getTypeFilters(selectedTaxonomy)
            }
            onSourceFilterChange={(value) =>
              handleOnSourceFilterSelected(value)
            }
            onTypeFilterChange={(value) =>
              handleOnTypeFilterSelected(selectedTaxonomy, value)
            }
            onSavedQuerySelected={(item, exploded) => {
              if (!exploded) {
                dispatch(addItemWithInfosInSelectedGroup(selectedTarget, item));
              } else {
                const newItem = {
                  ...item,
                  metadata: {
                    ...item.metadata,
                    target_query: mapQueryBlocksWithUniqueIdAndFormat(
                      item.metadata.target_query
                    ),
                  },
                };

                dispatch(addBlockInSelectedGroup(selectedTarget, newItem));
              }
            }}
            emptySavedQueriesListTitle="Sorry, we could not find any saved query matching your search"
            disableModals={[
              DATA_SOURCE_IDS.APPS,
              DATA_SOURCE_IDS.BROWSER,
              DATA_SOURCE_IDS.INSTAGRAM,
            ].includes(dataSource)}
          />
        </Loading>
      </Tab>,
    ];

    if (supportsSocial(dataSource)) {
      listOfTabs.push(
        <Tab
          title="Advanced options"
          key={ADVANCED_OPTIONS_TAB_ID}
          data-id={ADVANCED_OPTIONS_TAB_ID}
        >
          <QueryAdvancedOptions
            query={queryPreview}
            timeframes={timeframes}
            taxonomyList={taxonomyList}
            onAudienceTimeframeChange={handleAudienceTimeframeChangeRequest}
            onResultTimeframeChange={handleResultTimeframeChangeRequest}
            onChangeBaseQueryRequest={handleBaseQueryChangeRequest}
            onQueryTypeChange={handleQueryTypeChangeRequest}
          />
        </Tab>
      );
    }
    return listOfTabs;
  }, [
    areAvailableTypeFilters,
    currentQ,
    dataSource,
    dispatch,
    exploreItems,
    filteredDemographics,
    folders,
    taxonomyList,
    timeframes,
    getTypeFilters,
    handleOnTypeFilterSelected,
    handleOnTaxonomySelected,
    handleOnSourceFilterSelected,
    handleAddToQuery,
    handleBaseQueryChangeRequest,
    handleOnDemographicSelected,
    handleOnItemsSearch,
    handleAudienceTimeframeChangeRequest,
    handleOnItemDragToggle,
    handleOnLoadMoreItems,
    handleQueryTypeChangeRequest,
    handleResultTimeframeChangeRequest,
    selectedTaxonomy,
    selectedTarget,
    specifiedComponents,
    sourceFilters,
    shouldCallLoadMore,
    selectedType,
    selectedSource,
    queryPreview,
    filteredTaxonomyData,
    isTrial,
  ]);

  const handleOnTabChange = useCallback(
    (tab) => {
      const tabNodes = React.Children.toArray(tabs);
      const isAdvancedOptions =
        tabNodes[tab]?.props?.['data-id'] === ADVANCED_OPTIONS_TAB_ID;

      if (isAdvancedOptions) {
        return guardFeature(TRIAL_FEATURES.ADVANCED_OPTIONS, () => {
          setSelectedTab(tab);
        });
      }

      setSelectedTab(tab);
    },
    [tabs]
  );

  return (
    <React.Fragment>
      <StyledTaxonomyExplorerContainer>
        <Tabs
          data-test-id="tabs"
          onTabChange={handleOnTabChange}
          selectedTab={selectedTab}
        >
          {tabs}
        </Tabs>
      </StyledTaxonomyExplorerContainer>
    </React.Fragment>
  );
};

export default TaxonomyExplorerContainer;
